'use client';

import React, { useEffect, useState, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface CelebrationItem {
  memberId: string;
  memberName: string;
  type: 'birthday' | 'anniversary' | 'spouse_birthday';
  date: string;
  spouseName?: string;
  daysUntil: number;
  displayDate: string;
  photoUrl?: string;
}

interface CongratulationItem {
  id: string;
  senderName: string;
  recipientName: string;
  message: string;
  celebrationType: string;
  createdAt: string;
  imageUrl?: string;
}

interface EventItem {
  id: string;
  title: string;
  date: string;
  time: string;
  type: string | null;
  location: string;
  daysUntil: number;
  imageUrl?: string;
  speakers?: any; 
  meeting_link?: string;   
  modality: string; 
  description?: string;
}

interface MutualAidItem {
  id: string;
  memberName: string;
  postType: 'offer' | 'need';
  content: string;
  createdAt: string;
  memberAvatar?: string;
}

interface MemberRow {
  id: string;
  name: string;
  photo_url: string;
  birthday: string;
  wedding_anniversary: string;
  spouse_name: string;
  spouse_birthday: string;
  role_title: string;
}

interface LightboxData {
  title: string;
  subtitle: string;
  description?: string;
  imageUrl?: string;
  emoji?: string;
  badge?: string;
  location?: string;
  meetingLink?: string;
  modality?: string;
  rawSpeakers?: any; 
}

function getCDMXDate(): Date {
  const now = new Date();
  const cdmxStr = now.toLocaleString('en-US', { timeZone: 'America/Mexico_City' });
  return new Date(cdmxStr);
}

function getWeekStartISO(): string {
  const monday = getCDMXDate();
  const day = monday.getDay();
  const diff = monday.getDate() - day + (day === 0 ? -6 : 1); 
  monday.setDate(diff);
  
  const yyyy = monday.getFullYear();
  const mm = String(monday.getMonth() + 1).padStart(2, '0');
  const dd = String(monday.getDate()).padStart(2, '0');
  
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeToMMDD(raw: string | null | undefined): string | null {
  if (!raw || raw.trim() === '') return null;
  const cleaned = raw.trim();
  if (/^\d{2}-\d{2}$/.test(cleaned)) return cleaned;
  const isoMatch = cleaned.match(/^\d{4}-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}`;
  const slashParts = cleaned.split('/');
  if (slashParts.length === 2) {
    const [m, d] = slashParts;
    return `${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  if (slashParts.length === 3) {
    const [, m, d] = slashParts;
    return `${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return null;
}

function getDaysUntil(mmdd: string): number {
  const today = getCDMXDate();
  today.setHours(0, 0, 0, 0);
  const [month, day] = mmdd.split('-').map(Number);
  const thisYear = today.getFullYear();
  let target = new Date(thisYear, month - 1, day);
  target.setHours(0, 0, 0, 0);
  if (target < today) {
    target = new Date(thisYear + 1, month - 1, day);
  }
  return Math.floor((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function getDaysUntilFromISO(isoDate: string): number {
  const today = getCDMXDate();
  today.setHours(0, 0, 0, 0);
  const [year, month, day] = isoDate.split('-').map(Number);
  let target = new Date(year, month - 1, day);
  target.setHours(0, 0, 0, 0);
  return Math.floor((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function formatDisplayDate(mmdd: string): string {
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const [month, day] = mmdd.split('-').map(Number);
  return `${day} ${months[month - 1]}`;
}

function formatEventDate(isoDate: string): string {
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const [year, month, day] = isoDate.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

function parseSpeakers(speakersData: any): string {
  if (!speakersData) return '';
  if (typeof speakersData === 'string') return speakersData;
  if (Array.isArray(speakersData)) {
    if (speakersData.length === 0) return '';
    return speakersData
      .map(s => (typeof s === 'object' && s !== null ? s.name || s.nombre || '' : String(s)))
      .filter(Boolean)
      .join(', ');
  }
  return '';
}

function getSpeakersListWithBio(speakersData: any): { name: string; bio: string; company?: string }[] {
  if (!speakersData || !Array.isArray(speakersData)) return [];
  return speakersData.map(s => {
    if (typeof s === 'object' && s !== null) {
      return {
        name: s.name || s.nombre || 'Orador Invitado',
        bio: s.bio || s.reseña || s.description || 'Sin reseña disponible actualmente.',
        company: s.company || s.empresa || undefined
      };
    }
    return { name: String(s), bio: 'Sin reseña disponible actualmente.' };
  });
}

function getTypeConfig(type: CelebrationItem['type']) {
  switch (type) {
    case 'birthday':
      return { emoji: '🎂', label: 'Cumpleaños', accent: '#f43f5e', bg: 'from-rose-500/15 to-slate-900/90', border: 'rgba(244,63,94,0.35)', glow: 'shadow-[0_0_25px_rgba(244,63,94,0.2)]' };
    case 'anniversary':
      return { emoji: '💍', label: 'Aniversario', accent: '#a855f7', bg: 'from-purple-500/15 to-slate-900/90', border: 'rgba(168,85,247,0.35)', glow: 'shadow-[0_0_25px_rgba(168,85,247,0.2)]' };
    case 'spouse_birthday':
      return { emoji: '🌹', label: 'Esposo/a', accent: '#f59e0b', bg: 'from-amber-500/15 to-slate-900/90', border: 'rgba(245,158,11,0.35)', glow: 'shadow-[0_0_25px_rgba(245,158,11,0.2)]' };
  }
}

function getCongratTypeConfig(type: string) {
  switch (type) {
    case 'birthday': return { emoji: '🎂', label: 'Cumpleaños', accent: '#f43f5e', border: 'rgba(244,63,94,0.3)' };
    case 'anniversary': return { emoji: '💍', label: 'Aniversario', accent: '#a855f7', border: 'rgba(168,85,247,0.3)' };
    case 'spouse_birthday': return { emoji: '🌹', label: 'Esposo/a', accent: '#f59e0b', border: 'rgba(245,158,11,0.3)' };
    case 'custom': return { emoji: '✨', label: 'Personalizado', accent: '#6366f1', border: 'rgba(99,102,241,0.3)' };
    default: return { emoji: '🎉', label: 'Felicitación', accent: '#10b981', border: 'rgba(16,185,129,0.3)' };
  }
}

function getEventTypeConfig(type: string | null) {
  switch (type) {
    case 'sesion': return { emoji: '🏛️', label: 'Sesión', accent: '#38bdf8', border: 'rgba(56,189,248,0.35)' };
    case 'comite': return { emoji: '👥', label: 'Comité', accent: '#cbd5e1', border: 'rgba(203,213,225,0.35)' };
    case 'distrito': return { emoji: '🌐', label: 'Distrito', accent: '#c084fc', border: 'rgba(192,132,252,0.35)' };
    case 'convivencia': return { emoji: '🤝', label: 'Convivencia', accent: '#4ade80', border: 'rgba(74,222,128,0.35)' };
    case 'insignia': return { emoji: '⭐', label: 'Insignia', accent: '#facc15', border: 'rgba(250,204,21,0.35)' };
    default: return { emoji: '📅', label: 'Evento', accent: '#38bdf8', border: 'rgba(56,189,248,0.35)' };
  }
}

function CelebrationPill({ item, onClick }: { item: CelebrationItem; onClick: () => void }) {
  const config = getTypeConfig(item.type);
  const days = getDaysUntil(item.date);
  const dayInfo = days === 0 ? { label: '¡HOY!', urgent: true } : days === 1 ? { label: 'Mañana', urgent: true } : { label: `${days} días`, urgent: false };
  const title = item.type === 'anniversary'
    ? `${item.memberName}${item.spouseName ? ` & ${item.spouseName}` : ''}`
    : item.type === 'spouse_birthday'
    ? `${item.spouseName || 'Esposo/a'} de ${item.memberName}`
    : item.memberName;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className={`w-full relative min-h-[260px] sm:min-h-[290px] rounded-2xl overflow-hidden border bg-slate-900/70 backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400 flex flex-col justify-end ${days === 0 ? config.glow : 'shadow-lg shadow-black/40'}`}
      style={{ borderColor: config.border }}
    >
      <div className="absolute inset-0 w-full h-full z-0">
        {item.photoUrl ? (
          <img src={item.photoUrl} alt={title} className="w-full h-full object-cover object-top filter brightness-[0.8] contrast-[1.05]" loading="lazy" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-b ${config.bg} flex items-center justify-center`}><span className="text-4xl sm:text-5xl opacity-30">{config.emoji}</span></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
      </div>
      <div className="absolute top-3 left-3 z-10">
        <span className={`text-[9px] font-black px-2.5 py-1 rounded-md tracking-wider uppercase shadow-md border ${dayInfo.urgent ? 'bg-rose-500 text-white border-rose-400 animate-pulse' : 'bg-slate-900/90 text-slate-200 border-white/15 backdrop-blur-sm'}`}>
          {dayInfo.label}
        </span>
      </div>
      <div className="relative p-4 z-10 w-full min-w-0">
        <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: config.accent }}>{config.label}</p>
        <h4 className="text-xs sm:text-sm font-bold text-white leading-tight truncate drop-shadow-sm">{title}</h4>
        <p className="text-[10px] text-slate-300 mt-0.5 font-medium">{item.displayDate}</p>
      </div>
    </div>
  );
}

function CongratulationPill({ item, onClick }: { item: CongratulationItem; onClick: () => void }) {
  const config = getCongratTypeConfig(item.celebrationType);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className="w-full relative min-h-[260px] sm:min-h-[290px] rounded-2xl overflow-hidden border bg-slate-900/70 backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl shadow-lg shadow-black/40 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400 flex flex-col justify-end"
      style={{ borderColor: config.border }}
    >
      <div className="absolute inset-0 w-full h-full z-0">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt="Anuncio" className="w-full h-full object-cover filter brightness-[0.75]" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-b from-indigo-500/15 via-slate-900/80 to-slate-950 flex items-center justify-center"><span className="text-4xl sm:text-5xl opacity-30">{config.emoji}</span></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
      </div>
      <div className="relative p-4 z-10 w-full min-w-0">
        <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: config.accent }}>Felicitación</p>
        <h4 className="text-xs font-medium text-slate-100 leading-relaxed italic line-clamp-2 mb-2.5">"{item.message || `¡Felicidades!`}"</h4>
        <div className="pt-2 border-t border-white/10 text-[10px] text-slate-300 space-y-0.5">
          <p className="truncate">Para: <span className="text-white font-semibold">{item.recipientName}</span></p>
          <p className="text-[9px] text-slate-400 truncate">De: {item.senderName}</p>
        </div>
      </div>
    </div>
  );
}

function EventPill({ item, onClick }: { item: EventItem; onClick: () => void }) {
  const config = getEventTypeConfig(item.type);
  const dayInfo = item.daysUntil === 0 ? { label: '¡HOY!', urgent: true } : item.daysUntil === 1 ? { label: 'Mañana', urgent: true } : { label: `${item.daysUntil} días`, urgent: false };
  const stringSpeakers = parseSpeakers(item.speakers);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className="w-full relative min-h-[260px] sm:min-h-[290px] rounded-2xl overflow-hidden border bg-slate-900/70 backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl shadow-lg shadow-black/40 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400 flex flex-col justify-end"
      style={{ borderColor: config.border, boxShadow: item.daysUntil === 0 ? '0 0 25px rgba(56,189,248,0.25)' : undefined }}
    >
      <div className="absolute inset-0 w-full h-full z-0">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover filter brightness-[0.75]" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-b from-sky-500/15 via-slate-900/80 to-slate-950 flex items-center justify-center"><span className="text-4xl sm:text-5xl opacity-30">{config.emoji}</span></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
      </div>
      <div className="absolute top-3 left-3 z-10">
        <span className={`text-[9px] font-black px-2.5 py-1 rounded-md tracking-wider uppercase border ${dayInfo.urgent ? 'bg-sky-500 text-white border-sky-300 animate-pulse' : 'bg-slate-900/90 text-slate-200 border-white/15 backdrop-blur-sm'}`}>
          {dayInfo.label}
        </span>
      </div>
      <div className="relative p-4 z-10 w-full min-w-0">
        <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: config.accent }}>{config.label}</p>
        <h4 className="text-xs sm:text-sm font-bold text-white leading-tight uppercase line-clamp-2 mb-2">{item.title}</h4>

        <div className="flex flex-col gap-0.5 text-[10px] font-medium text-slate-300">
           <span className="text-sky-300 font-bold">{formatEventDate(item.date)} — {item.time} h</span>
           {stringSpeakers && <span className="text-violet-300 truncate italic">🎙️ {stringSpeakers}</span>}
           <span className="text-[9px] font-extrabold text-indigo-300 mt-1 tracking-wider uppercase flex items-center gap-1">
             <span>💻</span> {item.modality}
           </span>
        </div>
      </div>
    </div>
  );
}

function AidPill({ item, onClick }: { item: MutualAidItem; onClick: () => void }) {
  const isOffer = item.postType === 'offer';
  const borderStyle = isOffer ? 'rgba(34,197,94,0.35)' : 'rgba(99,102,241,0.35)';
  const accentColor = isOffer ? '#4ade80' : '#818cf8';
  const initials = item.memberAvatar || item.memberName.slice(0, 2).toUpperCase();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className="w-full relative min-h-[260px] sm:min-h-[290px] rounded-2xl overflow-hidden border bg-slate-900/80 backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl shadow-lg shadow-black/40 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400 flex flex-col justify-between p-4"
      style={{ borderColor: borderStyle }}
    >
      <div className="relative z-10 flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-full bg-slate-800 border border-white/15 flex items-center justify-center text-[9px] font-extrabold text-sky-400 flex-shrink-0 shadow-sm">
          {initials}
        </div>
        <span className="text-slate-200 font-semibold text-[11px] truncate">{item.memberName.split(' ')[0]}</span>
      </div>

      <div className="relative z-10 w-full min-w-0">
        <span 
          className="inline-block text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md mb-2 border shadow-sm"
          style={{ color: accentColor, borderColor: borderStyle, backgroundColor: isOffer ? 'rgba(34,197,94,0.1)' : 'rgba(99,102,241,0.1)' }}
        >
          {isOffer ? '🤝 Ofrezco' : '🙋 Necesito'}
        </span>
        <p className="text-[11px] text-slate-300 leading-relaxed line-clamp-4 italic break-words">
          "{item.content}"
        </p>
        <div className="mt-3 pt-2 border-t border-white/10 text-[8px] text-slate-400 uppercase tracking-widest font-extrabold">
          Tablón de Apoyo
        </div>
      </div>
    </div>
  );
}

function AdvertisementLightbox({ data, onClose }: { data: LightboxData; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<'details' | 'speaker'>('details');
  const speakersWithBio = useMemo(() => getSpeakersListWithBio(data.rawSpeakers), [data.rawSpeakers]);
  const hasSpeakers = speakersWithBio.length > 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 backdrop-blur-md bg-slate-950/80 transition-all duration-300 animate-fadeIn overflow-y-auto">
      <button 
        onClick={onClose} 
        aria-label="Cerrar modal"
        className="fixed top-4 right-4 z-50 w-10 h-10 rounded-2xl flex items-center justify-center bg-slate-900 border border-white/20 text-slate-300 hover:text-white hover:bg-slate-800 transition-all shadow-2xl focus:outline-none focus:ring-2 focus:ring-sky-400"
      >
        <Icon name="XMarkIcon" size={20} variant="outline" />
      </button>

      <div className="relative w-full max-w-lg rounded-2xl overflow-hidden border border-white/15 bg-slate-900 shadow-[0_20px_50px_rgba(0,0,0,0.9)] flex flex-col my-auto max-h-[85vh]">
        {data.imageUrl ? (
          <div className="w-full overflow-hidden flex items-center justify-center bg-slate-950 min-h-[140px] max-h-[240px] sm:max-h-[300px] flex-shrink-0 border-b border-white/10">
            <img src={data.imageUrl} alt={data.title} className="w-full h-full object-contain" />
          </div>
        ) : (
          <div className="w-full h-[120px] sm:h-[150px] flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950 border-b border-white/10 flex-shrink-0">
            <span className="text-5xl sm:text-6xl drop-shadow-md">{data.emoji || '📢'}</span>
          </div>
        )}

        {hasSpeakers && (
          <div className="w-full flex border-b border-white/10 bg-slate-900/90 sticky top-0 backdrop-blur-md z-10 flex-shrink-0">
            <button 
              onClick={() => setActiveTab('details')}
              className={`flex-1 py-3 text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all border-b-2 ${activeTab === 'details' ? 'text-sky-400 border-sky-400 bg-white/[0.03]' : 'text-slate-400 border-transparent hover:text-slate-200'}`}
            >
              📅 Detalles
            </button>
            <button 
              onClick={() => setActiveTab('speaker')}
              className={`flex-1 py-3 text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all border-b-2 ${activeTab === 'speaker' ? 'text-violet-400 border-violet-400 bg-white/[0.03]' : 'text-slate-400 border-transparent hover:text-slate-200'}`}
            >
              🎙️ Speaker ({speakersWithBio.length})
            </button>
          </div>
        )}

        <div className="w-full p-5 sm:p-6 bg-slate-900 overflow-y-auto text-left flex-1 scrollbar-thin">
          {activeTab === 'details' ? (
            <>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {data.badge && (
                  <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-md tracking-wider uppercase bg-violet-500/15 text-violet-300 border border-violet-500/30">
                    {data.badge}
                  </span>
                )}
                {data.modality && (
                  <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-md tracking-wider uppercase bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
                    {data.modality}
                  </span>
                )}
              </div>
              
              <h3 className="text-white font-bold text-base sm:text-lg tracking-tight uppercase break-words leading-snug">{data.title}</h3>
              <p className="text-sky-400 font-bold text-xs sm:text-sm mt-1">{data.subtitle}</p>
              
              {data.location && (
                <p className="text-emerald-400 text-xs sm:text-sm font-semibold mt-3 flex items-center gap-1.5">
                  <span>📍 Lugar:</span>
                  <span className="truncate">{data.location}</span>
                </p>
              )}

              {data.meetingLink && (
                <a
                  href={data.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 w-full justify-center px-4 py-2.5 rounded-xl bg-emerald-600 border border-emerald-400/30 text-white font-bold text-xs hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-950/50 truncate"
                >
                  <span>📹 Enlace a la Reunión Virtual</span>
                </a>
              )}

              {data.description && (
                <div className="mt-4 border-l-2 border-sky-400/50 pl-3.5 bg-slate-950/40 p-3 rounded-r-xl border-y border-r border-white/5">
                  <p className="text-slate-300 text-xs sm:text-sm font-normal whitespace-pre-line leading-relaxed break-words">
                    {data.description}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              {speakersWithBio.map((sp, idx) => (
                <div key={idx} className="border-b border-white/10 pb-3 last:border-none last:pb-0">
                  <h4 className="text-white font-bold text-xs sm:text-sm uppercase tracking-tight">{sp.name}</h4>
                  {sp.company && (
                    <p className="text-violet-400 text-[10px] font-semibold uppercase tracking-wider">{sp.company}</p>
                  )}
                  <p className="text-slate-300 text-xs mt-2 leading-relaxed whitespace-pre-line bg-slate-950/50 p-3 border border-white/10 rounded-xl italic break-words">
                    "{sp.bio}"
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CelebrationsBanner() {
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [congratulations, setCongratulations] = useState<CongratulationItem[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [aidPosts, setAidPosts] = useState<MutualAidItem[]>([]); 
  const [loading, setLoading] = useState(true);
  const [activeLightbox, setActiveLightbox] = useState<LightboxData | null>(null);

  useEffect(() => {
    const supabase = createClient();
    async function fetchData() {
      try {
        const today = getCDMXDate();
        today.setHours(0, 0, 0, 0);
        const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        
        const future60 = new Date(today);
        future60.setDate(future60.getDate() + 60);
        const future60ISO = `${future60.getFullYear()}-${String(future60.getMonth() + 1).padStart(2, '0')}-${String(future60.getDate()).padStart(2, '0')}`;
        
        const weekStartStr = getWeekStartISO();

        const [membersRes, congratsRes, eventsRes, aidRes] = await Promise.all([
          supabase.from('members').select('id, name, photo_url, birthday, wedding_anniversary, spouse_name, spouse_birthday, role_title').eq('status', 'activo'),
          supabase.from('congratulations').select('id, sender_name, recipient_name, message, celebration_type, created_at, image_url').gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()).order('created_at', { ascending: false }).limit(20),
          supabase.from('events').select('id, title, date, time, type, location, image_url, speakers, meeting_link, modality, description').gte('date', todayISO).lte('date', future60ISO).order('date', { ascending: true }).limit(15),
          supabase.from('mutual_aid_posts').select('id, member_name, post_type, content, created_at, member_avatar').eq('week_start', weekStartStr).order('created_at', { ascending: false })
        ]);

        if (!membersRes.error && membersRes.data) setMembers(membersRes.data as MemberRow[]);
        
        if (!congratsRes.error && congratsRes.data) {
          setCongratulations(congratsRes.data.map((r: any) => ({
            id: r.id, senderName: r.sender_name, recipientName: r.recipient_name, message: r.message, celebrationType: r.celebration_type, createdAt: r.created_at, imageUrl: r.image_url || undefined
          })));
        }
        
        if (!eventsRes.error && eventsRes.data) {
          setEvents(eventsRes.data.map((r: any) => ({ 
            id: r.id, title: r.title, date: r.date, time: r.time, type: r.type, location: r.location, daysUntil: getDaysUntilFromISO(r.date), imageUrl: r.image_url || undefined, speakers: r.speakers, meeting_link: r.meeting_link || undefined, modality: r.modality, description: r.description || undefined
          })));
        }

        if (!aidRes.error && aidRes.data) {
          setAidPosts(aidRes.data.map((r: any) => ({
            id: r.id, memberName: r.member_name, postType: r.post_type as 'offer' | 'need', content: r.content, createdAt: r.created_at, memberAvatar: r.member_avatar || undefined
          })));
        }
      } catch (err) {
        console.error("Error cargando cartelera social unificada:", err);
      } finally { setLoading(false); }
    }

    fetchData();

    const channelCongrats = supabase.channel('congratulations-changes').on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'congratulations' }, (payload) => {
      const r = payload.new as any;
      setCongratulations((prev) => [{ id: r.id, senderName: r.sender_name, recipientName: r.recipient_name, message: r.message, celebrationType: r.celebration_type, createdAt: r.created_at, imageUrl: r.image_url || undefined }, ...prev.slice(0, 19)]);
    }).subscribe();

    const channelAid = supabase.channel('mutual-aid-changes').on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'mutual_aid_posts' }, (payload) => {
      const r = payload.new as any;
      setAidPosts((prev) => [{ id: r.id, memberName: r.member_name, postType: r.post_type as 'offer' | 'need', content: r.content, createdAt: r.created_at, memberAvatar: r.member_avatar || undefined }, ...prev]);
    }).subscribe();

    return () => { 
      supabase.removeChannel(channelCongrats); 
      supabase.removeChannel(channelAid); 
    };
  }, []);

  const celebrations = useMemo<CelebrationItem[]>(() => {
    const items: CelebrationItem[] = [];
    members.forEach((m) => {
      const birthdayMMDD = normalizeToMMDD(m.birthday);
      if (birthdayMMDD) { const days = getDaysUntil(birthdayMMDD); if (days <= 60) items.push({ memberId: m.id, memberName: m.name, type: 'birthday', date: birthdayMMDD, daysUntil: days, displayDate: formatDisplayDate(birthdayMMDD), photoUrl: m.photo_url || undefined }); }
      const anniversaryMMDD = normalizeToMMDD(m.wedding_anniversary);
      if (anniversaryMMDD) { const days = getDaysUntil(anniversaryMMDD); if (days <= 60) items.push({ memberId: m.id, memberName: m.name, type: 'anniversary', date: anniversaryMMDD, spouseName: m.spouse_name || undefined, daysUntil: days, displayDate: formatDisplayDate(anniversaryMMDD), photoUrl: m.photo_url || undefined }); }
      const spouseBirthdayMMDD = normalizeToMMDD(m.spouse_birthday);
      if (spouseBirthdayMMDD) { const days = getDaysUntil(spouseBirthdayMMDD); if (days <= 60) items.push({ memberId: `${m.id}-spouse`, memberName: m.name, type: 'spouse_birthday', date: spouseBirthdayMMDD, spouseName: m.spouse_name || undefined, daysUntil: days, displayDate: formatDisplayDate(spouseBirthdayMMDD), photoUrl: m.photo_url || undefined }); }
    });
    return items.sort((a, b) => a.daysUntil - b.daysUntil);
  }, [members]);

  const todayCount = celebrations.filter((c) => getDaysUntil(c.date) === 0).length;
  const todayEvents = events.filter((e) => e.daysUntil === 0).length;
  const totalItems = celebrations.length + congratulations.length + events.length + aidPosts.length;

  const isUrgent = todayCount > 0 || todayEvents > 0;
  const headlineText = loading ? 'Cargando cartelera...' : isUrgent ? '¡Cartelera de Hoy!' : 'Cartelera Unificada';

  return (
    <div className="min-h-screen w-full bg-slate-950 text-white p-3 sm:p-6 md:p-8 relative overflow-x-hidden">
      {/* Esferas de luz sutiles para crear profundidad en el fondo */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-30 z-0">
        <div className="absolute -top-40 left-1/4 w-80 sm:w-[500px] h-80 sm:h-[500px] rounded-full blur-[120px]" style={{ background: 'radial-gradient(circle, #0284c7, transparent 70%)' }} />
        <div className="absolute top-1/2 right-10 w-80 sm:w-[500px] h-80 sm:h-[500px] rounded-full blur-[140px]" style={{ background: 'radial-gradient(circle, #6366f1, transparent 70%)' }} />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col">
        <div className="mb-5 sm:mb-8 border-b border-white/10 pb-4">
          <h3 className="text-white font-extrabold text-xl sm:text-2xl md:text-3xl tracking-tight bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">{headlineText}</h3>
          {!loading && <p className="text-slate-400 text-xs sm:text-sm font-medium mt-1">Actividades, networking, ayudas, fechas especiales del club</p>}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
            {[...Array(4)].map((_, i) => <div key={i} className="h-[260px] sm:h-[290px] w-full rounded-2xl bg-slate-900/60 border border-white/10 animate-pulse" />)}
          </div>
        ) : totalItems === 0 ? (
          <div className="py-12 text-center bg-slate-900/40 border border-white/10 rounded-2xl w-full"><p className="text-slate-400 text-sm font-semibold">No hay publicaciones activas esta semana</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full min-w-0">
            
            {/* 1. Eventos */}
            {events.map((item) => (
              <div key={item.id} className="w-full min-w-0">
                <EventPill 
                  item={item} 
                  onClick={() => setActiveLightbox({
                    title: item.title,
                    subtitle: `${formatEventDate(item.date)} a las ${item.time} h`,
                    description: item.description,
                    imageUrl: item.imageUrl,
                    badge: item.type || 'Evento',
                    emoji: '📅',
                    location: item.location,
                    meetingLink: item.meeting_link || undefined,
                    modality: `Modalidad: ${item.modality.toUpperCase()}`,
                    rawSpeakers: item.speakers 
                  })}
                />
              </div>
            ))}

            {/* 2. Apoyo Mutuo */}
            {aidPosts.map((item) => (
              <div key={item.id} className="w-full min-w-0">
                <AidPill 
                  item={item}
                  onClick={() => setActiveLightbox({
                    title: item.postType === 'offer' ? `🤝 ${item.memberName} Ofrece:` : `🙋 ${item.memberName} Necesita apoyo:`,
                    subtitle: 'Publicación Semanal de Apoyo Mutuo',
                    description: item.content,
                    badge: item.postType === 'offer' ? 'Ofrecimiento' : 'Necesidad',
                    emoji: item.postType === 'offer' ? '🤝' : '🙋'
                  })}
                />
              </div>
            ))}

            {/* 3. Celebraciones */}
            {celebrations.map((item) => {
              const labelTitle = item.type === 'anniversary' ? 'Aniversario de Bodas' : item.type === 'spouse_birthday' ? 'Cumpleaños de Pareja' : 'Cumpleaños';
              return (
                <div key={`${item.memberId}-${item.type}`} className="w-full min-w-0">
                  <CelebrationPill 
                    item={item} 
                    onClick={() => setActiveLightbox({
                      title: item.memberName,
                      subtitle: `${labelTitle} — ${item.displayDate}`,
                      imageUrl: item.photoUrl,
                      badge: 'Fecha Especial',
                      emoji: item.type === 'anniversary' ? '💍' : '🎂'
                    })}
                  />
                </div>
              );
            })}

            {/* 4. Felicitaciones */}
            {congratulations.map((item) => (
              <div key={item.id} className="w-full min-w-0">
                <CongratulationPill 
                  item={item} 
                  onClick={() => setActiveLightbox({
                    title: `Para: ${item.recipientName}`,
                    subtitle: `Enviado por: ${item.senderName}`,
                    description: item.message,
                    imageUrl: item.imageUrl,
                    badge: 'Felicitación Directa',
                    emoji: '🎉'
                  })}
                />
              </div>
            ))}

          </div>
        )}
      </div>

      {activeLightbox && <AdvertisementLightbox data={activeLightbox} onClose={() => setActiveLightbox(null)} />}
    </div>
  );
}